import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ENTITY_SELECT_LIST_PARAMS } from "@/core/api/use-paged-select-options";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { DEFAULT_CURRENCY, majorToMinor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateQrCode } from "@/features/merchant/api/use-create-qr-code";
import type { CreateQrCodePayload, QrCodeDto } from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createQrCodeSchema,
  type QrCodeFormValues,
} from "@/features/merchant/schemas/qr-code.schema";
import { QR_TYPES } from "@/features/shared/api/enums";
import { useMerchantStores } from "@/features/merchant/api/use-merchant-stores";

const EMPTY_VALUES: QrCodeFormValues = {
  storeId: "",
  qrType: "STATIC",
  amount: undefined,
  currency: DEFAULT_CURRENCY,
  expiresAt: "",
};

/**
 * Génération d'un QR code marchand
 * (`POST /QrCodes/merchant/{merchantId}/add`). Le QR statique n'a pas de
 * montant fixé (payé au montant scanné par le client) ; le QR dynamique
 * fixe le montant côté serveur.
 */
export function QrCodeFormDialog({
  open,
  onOpenChange,
  merchantId,
  actingMerchantId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  actingMerchantId?: string;
  onCreated?: (qr: QrCodeDto) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createQrCodeSchema(), [i18n.language]);

  const createQrCode = useCreateQrCode(merchantId);
  const pending = createQrCode.isPending;
  const { data: stores, isLoading: storesLoading } = useMerchantStores(
    merchantId,
    ENTITY_SELECT_LIST_PARAMS,
    open
  );
  const storeOptions = useMemo(
    () =>
      (stores?.items ?? [])
        .filter((store) => Boolean(store.id))
        .map((store) => ({
          value: store.id as string,
          label: [store.name, store.address].filter(Boolean).join(" · "),
          description: store.id,
        })),
    [stores]
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const currency = parsed.data.currency?.trim() || DEFAULT_CURRENCY;
      const payload: CreateQrCodePayload = {
        storeId: parsed.data.storeId?.trim() || undefined,
        qrType: parsed.data.qrType,
        amountMinor:
          parsed.data.qrType === "DYNAMIC" && parsed.data.amount
            ? majorToMinor(parsed.data.amount, currency)
            : undefined,
        currency:
          parsed.data.qrType === "DYNAMIC" ? currency : undefined,
        expiresAt: parsed.data.expiresAt?.trim() || undefined,
      };
      createQrCode.mutate(
        { body: payload, actingMerchantId },
        {
          onSuccess: (created) => {
            notifSilence(tm("merchant.qrCodes.feedback.created"));
            onOpenChange(false);
            onCreated?.(created);
          },
          onError: (error) => notifSilenceError(error.message),
        }
      );
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tm("merchant.qrCodes.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="qrType">
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tm("merchant.qrCodes.form.qrType")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as QrCodeFormValues["qrType"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QR_TYPES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="storeId">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="qr-store-id">
                  {tm("merchant.qrCodes.form.storeId")}
                </Label>
                <EntitySearchableSelect
                  id="qr-store-id"
                  options={storeOptions}
                  value={field.state.value ?? ""}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tm("merchant.qrCodes.form.storeIdPlaceholder")}
                  isLoading={storesLoading}
                  clearable
                />
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={(state) => state.values.qrType}>
            {(qrType) => (
              <>
                {qrType === "DYNAMIC" && (
                  <>
                    <form.Field name="amount">
                      {(field) => (
                        <div className="space-y-1.5">
                          <Label htmlFor="qr-amount">
                            {tm("merchant.qrCodes.form.amount")}
                          </Label>
                          <Input
                            id="qr-amount"
                            className="notranslate"
                            type="number"
                            min={0}
                            value={
                              typeof field.state.value === "number"
                                ? field.state.value
                                : ""
                            }
                            placeholder={tm(
                              "merchant.qrCodes.form.amountPlaceholder"
                            )}
                            onBlur={field.handleBlur}
                            onChange={(e) =>
                              field.handleChange(e.target.valueAsNumber)
                            }
                          />
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="currency">
                      {(field) => (
                        <div className="space-y-1.5">
                          <Label htmlFor="qr-currency">
                            {tm("merchant.qrCodes.form.currency")}
                          </Label>
                          <Input
                            id="qr-currency"
                            className="notranslate uppercase"
                            maxLength={3}
                            value={field.state.value ?? ""}
                            placeholder={tm(
                              "merchant.qrCodes.form.currencyPlaceholder"
                            )}
                            onBlur={field.handleBlur}
                            onChange={(e) =>
                              field.handleChange(e.target.value.toUpperCase())
                            }
                          />
                        </div>
                      )}
                    </form.Field>
                  </>
                )}
              </>
            )}
          </form.Subscribe>

          <form.Field name="expiresAt">
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="qr-expires-at">
                  {tm("merchant.qrCodes.form.expiresAt")}
                </Label>
                <Input
                  id="qr-expires-at"
                  className="notranslate"
                  value={field.state.value ?? ""}
                  placeholder={tm("merchant.qrCodes.form.expiresAtPlaceholder")}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
